import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AddressLookupService as msAddressLookupService } from '@yol-digital/ms-client';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { ENVIRONMENT_URLS_CONFIG_TOKEN, EnvironmentUrlsConfig } from '../index';
import Api = msAddressLookupService.Api;

@Injectable({ providedIn: 'root' })
export class AddressLookupService {
  private httpClient = inject(HttpClient);
  private config = inject<EnvironmentUrlsConfig>(ENVIRONMENT_URLS_CONFIG_TOKEN);
  private api = new Api(this.config.newMicroServiceEndpoint, this.httpClient);
  public addressFormValidations$ = new BehaviorSubject(false);

  public findCities(postcode: string, isLineCheck = true): Observable<string[]> {
    if (!postcode || postcode.length !== 4) return of([]);

    let params;
    params = {
      postcode,
    };

    if (isLineCheck) {
      params = {
        ...params,
        lookupMethod: 'linecheck',
      };
    }

    return this.api.publicV1.cityV1(params).pipe(
      shareReplay(1),
      map((resp: msAddressLookupService.AddressLookupResp) => resp.cities)
    );
  }

  public findStreetNames(
    postcode: string,
    city: string,
    streetPrefix: string,
    isLineCheck = true
  ): Observable<msAddressLookupService.StreetNameAndId[]> {
    if (!postcode || postcode.length !== 4) return undefined;

    let params;
    params = {
      postcode,
      city,
      streetName: streetPrefix,
    };

    if (isLineCheck) {
      params = {
        ...params,
        lookupMethod: 'linecheck',
      };
    }
    return this.api.publicV1.streetV1(params).pipe(
      shareReplay(1),
      map((resp: msAddressLookupService.StreetNameAndId[]) => resp as msAddressLookupService.StreetNameAndId[])
    );
  }

  public findStreetNumbers(
    postcode: string,
    city: string,
    street: string,
    streetNumberPrefix: string,
    isLineCheck = true
  ): Observable<string[]> {
    if (!postcode || postcode.length !== 4) return of([]);

    const baseParams = {
      postcode,
      city,
      streetNumber: streetNumberPrefix,
      streetName: street,
    };

    let streetNumberV1Call$ = this.api.publicV1.streetNumberV1(baseParams);

    if (isLineCheck) {
      streetNumberV1Call$ = this.findStreetNames(postcode, city, street).pipe(
        switchMap((streetNames: msAddressLookupService.StreetNameAndId[]) => {
          const matchingStreet = streetNames?.find(streetResponse => street === streetResponse.streetName);

          const params: msAddressLookupService.AddressLookupStreetNumberReqV1 = {
            ...baseParams,
            lookupMethod: 'linecheck',
            streetId: matchingStreet?.streetId?.toString(),
          };

          return this.api.publicV1.streetNumberV1(params);
        })
      );
    }

    return streetNumberV1Call$.pipe(
      shareReplay(1),
      map((resp: msAddressLookupService.AddressLookupResp) => resp.streetNumbers)
    );
  }
}
